import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  X, Loader2, CheckCircle2, ArrowRight, SkipForward, Sparkles,
  History, Repeat, Wallet, AlertTriangle,
} from 'lucide-react';

const CLASSIFICATIONS = [
  { key: 'income', label: 'Income', color: 'bg-green-100 text-green-700 border-green-300', icon: '💰' },
  { key: 'expense', label: 'Expense', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: '🧾' },
  { key: 'payroll', label: 'Payroll', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: '👥' },
  { key: 'rent', label: 'Rent', color: 'bg-indigo-100 text-indigo-700 border-indigo-300', icon: '🏠' },
  { key: 'utility', label: 'Utility', color: 'bg-cyan-100 text-cyan-700 border-cyan-300', icon: '⚡' },
  { key: 'tax', label: 'Tax', color: 'bg-rose-100 text-rose-700 border-rose-300', icon: '📊' },
  { key: 'transfer', label: 'Transfer', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: '↔️' },
  { key: 'fee', label: 'Bank fee', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: '💳' },
  { key: 'personal', label: 'Personal', color: 'bg-pink-100 text-pink-700 border-pink-300', icon: '👤' },
];

export default function ReviewTransactionsModal({ open, onClose, statementId, onComplete }) {
  const { api } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [classification, setClassification] = useState('expense');
  const [note, setNote] = useState('');
  const [applyToSimilar, setApplyToSimilar] = useState(true);
  const [saveRule, setSaveRule] = useState(true);
  const [stats, setStats] = useState({ confirmed: 0, similarApplied: 0 });

  const fetchNext = useCallback(async () => {
    setLoading(true);
    try {
      const params = statementId ? { statement_id: statementId } : {};
      const res = await api.get('/bank-statements/review/next', { params });
      setCard(res.data);
      if (res.data.transaction) {
        setClassification(res.data.suggested_classification || 'expense');
        setNote(res.data.transaction.manual_note || '');
      }
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [api, statementId]);

  useEffect(() => { if (open) fetchNext(); }, [open, fetchNext]);

  const submit = async () => {
    if (!card?.transaction) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/bank-statements/transactions/${card.transaction.id}/confirm`, {
        classification,
        manual_note: note,
        apply_to_similar: applyToSimilar,
        save_rule: saveRule,
      });
      const similar = res.data.similar_updated || 0;
      setStats(s => ({ confirmed: s.confirmed + 1, similarApplied: s.similarApplied + similar }));
      if (similar > 0) {
        toast.success(`Confirmed + applied to ${similar} similar transaction${similar === 1 ? '' : 's'}`);
      } else {
        toast.success('Confirmed');
      }
      fetchNext();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  const skip = async () => {
    if (!card?.transaction) return;
    setSubmitting(true);
    try {
      await api.post(`/bank-statements/transactions/${card.transaction.id}/skip`);
      fetchNext();
    } catch { toast.error('Skip failed'); }
    finally { setSubmitting(false); }
  };

  const revisitSkipped = async () => {
    setSubmitting(true);
    try {
      const res = await api.post('/bank-statements/review/reset-skipped');
      toast.success(`Reset ${res.data.reset} skipped transaction${res.data.reset === 1 ? '' : 's'}`);
      fetchNext();
    } catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const handleClose = () => {
    if (onComplete && (stats.confirmed > 0 || stats.similarApplied > 0)) onComplete();
    setStats({ confirmed: 0, similarApplied: 0 });
    onClose();
  };

  if (!open) return null;

  const txn = card?.transaction;
  const remaining = card?.remaining || 0;
  const progress = stats.confirmed > 0 ? Math.round((stats.confirmed / (stats.confirmed + remaining)) * 100) : 0;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
      data-testid="review-modal"
    >
      <div
        className="bg-background rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> Review &amp; Categorise
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {stats.confirmed > 0 && <span className="text-green-600">{stats.confirmed} confirmed · </span>}
              {stats.similarApplied > 0 && <span className="text-blue-600">{stats.similarApplied} auto-applied · </span>}
              <span data-testid="remaining-count">{remaining} remaining</span>
            </p>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-muted rounded" data-testid="close-review">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        {stats.confirmed > 0 && (
          <div className="h-1 bg-muted">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>
          ) : !txn ? (
            <div className="text-center py-12" data-testid="all-done">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold">All done!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {stats.confirmed > 0
                  ? `You categorised ${stats.confirmed} transaction${stats.confirmed === 1 ? '' : 's'}, with ${stats.similarApplied} more auto-applied to similar items.`
                  : 'Nothing left to review.'}
              </p>
              {card?.skipped_count > 0 && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm" data-testid="skipped-prompt">
                  <p className="text-amber-800 font-medium">
                    You have {card.skipped_count} skipped transaction{card.skipped_count === 1 ? '' : 's'}.
                  </p>
                  <p className="text-xs text-amber-700 mt-1">Want to revisit them now that you might have more context?</p>
                  <Button size="sm" variant="outline" className="mt-2" onClick={revisitSkipped} disabled={submitting} data-testid="revisit-skipped-btn">
                    <SkipForward className="w-3 h-3 mr-1" /> Revisit skipped
                  </Button>
                </div>
              )}
              <Button className="mt-6" onClick={handleClose} data-testid="done-btn">Close</Button>
            </div>
          ) : (
            <>
              {/* Transaction card */}
              <Card className="border-2" data-testid="txn-card">
                <CardContent className="py-5">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-muted-foreground">{txn.date}</div>
                      <h3 className="text-lg font-bold mt-0.5 break-words" data-testid="card-payee">{txn.payee_name}</h3>
                      {txn.description && txn.description !== txn.payee_name && (
                        <p className="text-xs text-muted-foreground mt-1 break-words">{txn.description}</p>
                      )}
                    </div>
                    <div className={`text-2xl font-bold font-mono whitespace-nowrap ${txn.amount < 0 ? 'text-red-600' : 'text-green-600'}`} data-testid="card-amount">
                      {txn.amount < 0 ? '−' : '+'}£{Math.abs(txn.amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>

                  {/* Context badges */}
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {txn.balance != null && (
                      <span className="flex items-center gap-1"><Wallet className="w-3 h-3" /> Balance: £{txn.balance.toLocaleString()}</span>
                    )}
                    {txn.type && <Badge variant="outline" className="text-[10px]">{txn.type}</Badge>}
                    {card.similar_count > 0 && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <Repeat className="w-3 h-3" /> {card.similar_count} similar unreviewed
                      </span>
                    )}
                    {card.existing_rule && (
                      <span className="flex items-center gap-1 text-purple-600">
                        <Sparkles className="w-3 h-3" /> Rule exists ({card.existing_rule.classification})
                      </span>
                    )}
                  </div>

                  {/* History from same payee */}
                  {card.history && card.history.length > 0 && (
                    <div className="mt-3 p-2 bg-muted/40 rounded text-xs">
                      <div className="font-medium flex items-center gap-1 mb-1">
                        <History className="w-3 h-3" /> Recent {txn.payee_name} (already reviewed)
                      </div>
                      {card.history.slice(0, 3).map((h, i) => (
                        <div key={i} className="flex items-center justify-between text-muted-foreground">
                          <span>{h.date} · {h.classification}</span>
                          <span className={h.amount < 0 ? 'text-red-600' : 'text-green-600'}>
                            {h.amount < 0 ? '−' : '+'}£{Math.abs(h.amount).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Classification chips */}
              <div className="mt-5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Categorise as</Label>
                <div className="grid grid-cols-3 gap-2 mt-2" data-testid="classification-grid">
                  {CLASSIFICATIONS.map(c => (
                    <button
                      key={c.key}
                      onClick={() => setClassification(c.key)}
                      className={`p-2 rounded-lg border-2 transition-all text-sm ${
                        classification === c.key
                          ? `${c.color} ring-2 ring-offset-2 ring-current`
                          : 'border-border hover:border-primary/40 bg-background'
                      }`}
                      data-testid={`class-${c.key}`}
                    >
                      <div className="text-lg">{c.icon}</div>
                      <div className="font-medium">{c.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div className="mt-4">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Note (optional)</Label>
                <Input
                  className="mt-1"
                  placeholder="e.g. Owner draw, supplier deposit, weekly takings…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid="note-input"
                />
              </div>

              {/* Switches */}
              <div className="mt-4 space-y-3 p-3 bg-muted/30 rounded-md">
                <div className={`flex items-center justify-between ${card.similar_count >= 5 ? 'p-2 -m-1 bg-amber-50 border border-amber-200 rounded' : ''}`}>
                  <div className="flex-1 mr-3">
                    <Label className="text-sm flex items-center gap-1">
                      Apply to {card.similar_count} similar unreviewed
                      {card.similar_count >= 5 && applyToSimilar && (
                        <AlertTriangle className="w-3 h-3 text-amber-600" />
                      )}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {card.similar_count > 0
                        ? <>Other unreviewed transactions from <span className="font-medium">{txn.payee_name}</span> will be set to <span className="font-medium">{classification}</span> too.</>
                        : <>No similar unreviewed transactions found.</>}
                    </p>
                  </div>
                  <Switch checked={applyToSimilar} onCheckedChange={setApplyToSimilar} disabled={card.similar_count === 0} data-testid="apply-similar-toggle" />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex-1 mr-3">
                    <Label className="text-sm">Remember for future uploads</Label>
                    <p className="text-xs text-muted-foreground">Save a rule so the next time this payee shows up, it&apos;s pre-classified.</p>
                  </div>
                  <Switch checked={saveRule} onCheckedChange={setSaveRule} data-testid="save-rule-toggle" />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {txn && (
          <div className="border-t p-4 flex items-center justify-between gap-2 bg-muted/20">
            <Button variant="ghost" size="sm" onClick={skip} disabled={submitting} data-testid="skip-btn">
              <SkipForward className="w-4 h-4 mr-1" /> Skip
            </Button>
            <Button onClick={submit} disabled={submitting} className="flex-1 max-w-xs" data-testid="confirm-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Confirm &amp; Next <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
